import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeftLong, faSave } from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Spinner } from 'react-bootstrap';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import Container from '../../../components/ui/Container/Container';
import ModalDialog from '../../../components/ui/ModalDialog/ModalDialog';
import useMembershipTypeEditor from './useMembershipTypeEditor';
import styles from './MembershipTypeEditor.module.css';

interface IMembershipTypeEditorProps {
  membershipTypeId?: string;
}

export const MembershipTypeEditor = ({ membershipTypeId = '' }: IMembershipTypeEditorProps) => {
  const manager = useMembershipTypeEditor(membershipTypeId);
  const ToastContainer = manager.ToastContainer;

  if (!membershipTypeId) return <></>;

  return (
    <>
      <ToastContainer />
      <ModalDialog
        showDialog={manager.showUnsavedChangesDialog}
        heading="Unsaved Changes"
        message="You have unsaved changes. Do you want to discard them?"
        okCallback={manager.discardUnsavedChanges}
        okButtonText="Yes, discard changes"
        cancelCallback={manager.closeUnsavedChangesDialog}
        cancelButtonText="No, keep editing"
      />
      <LoadingContainer isLoading={manager.loadingMembershipType || manager.loadingOptions}>
        <Container className={styles.editorContainer}>
          {/* Header Section */}
          <div className={styles.header}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                manager.back();
              }}
              className={styles.backLink}>
              <FontAwesomeIcon icon={faArrowLeftLong} className="me-2" />
              Back to Membership Types
            </a>
            <h2 className={styles.title}>
              {manager.membershipType.id === 0 ? 'Create' : 'Edit'} Membership Type
            </h2>
          </div>

          {/* Main Form */}
          <Row className={styles.formRow}>
            <Col md={8} sm={12} className={styles.formColumn}>
              <div className={styles.card}>
                <div className="row mb-2">
                  <div className="col-md-12">
                    <label htmlFor="name" className={styles.label}>
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      className={`form-control ${manager.fieldErrors.includes('overview:name') ? 'is-invalid' : ''}`}
                      value={manager.membershipType.name}
                      onChange={(e) => manager.updateValue('name', e.target.value)}
                      placeholder="e.g., Gold Membership"
                    />
                    {manager.fieldErrors.includes('overview:name') && (
                      <div className="invalid-feedback">Name is required.</div>
                    )}
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-6">
                    <label htmlFor="categoryCodeId" className={styles.label}>
                      Category
                    </label>
                    <select
                      id="categoryCodeId"
                      className={`form-select ${manager.fieldErrors.includes('overview:categoryCodeId') ? 'is-invalid' : ''}`}
                      value={
                        manager.membershipType.categoryCodeId === null
                          ? ''
                          : manager.membershipType.categoryCodeId
                      }
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : Number(e.target.value);
                        manager.updateValue('categoryCodeId', value);
                      }}>
                      <option value="">- Select Category -</option>
                      {manager.categoryOptions.map((option) => {
                        return (
                          <option key={option.id} value={option.id}>
                            {option.codeValue}
                          </option>
                        );
                      })}
                    </select>
                    {manager.fieldErrors.includes('overview:categoryCodeId') && (
                      <div className="invalid-feedback">Category is required.</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="classCodeId" className={styles.label}>
                      Class
                    </label>
                    <select
                      id="classCodeId"
                      className={`form-select ${manager.fieldErrors.includes('overview:classCodeId') ? 'is-invalid' : ''}`}
                      value={
                        manager.membershipType.classCodeId === null
                          ? ''
                          : manager.membershipType.classCodeId
                      }
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : Number(e.target.value);
                        manager.updateValue('classCodeId', value);
                      }}>
                      <option value="">- Select Class -</option>
                      {manager.classOptions.map((option) => {
                        return (
                          <option key={option.id} value={option.id}>
                            {option.codeValue}
                          </option>
                        );
                      })}
                    </select>
                    {manager.fieldErrors.includes('overview:classCodeId') && (
                      <div className="invalid-feedback">Class is required.</div>
                    )}
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-6">
                    <label htmlFor="initiationFee" className={styles.label}>
                      Initiation Fee ($)
                    </label>
                    <input
                      type="number"
                      id="initiationFee"
                      min="0"
                      step="0.01"
                      className={`form-control ${manager.fieldErrors.includes('overview:initiationFee') ? 'is-invalid' : ''}`}
                      value={manager.membershipType.initiationFee}
                      onChange={(e) =>
                        manager.updateValue('initiationFee', parseFloat(e.target.value))
                      }
                      placeholder="0.00"
                    />
                    {manager.fieldErrors.includes('overview:initiationFee') && (
                      <div className="invalid-feedback">Initiation Fee must be positive.</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="dues" className={styles.label}>
                      Annual Dues ($)
                    </label>
                    <input
                      type="number"
                      id="dues"
                      min="0"
                      step="0.01"
                      className={`form-control ${manager.fieldErrors.includes('overview:dues') ? 'is-invalid' : ''}`}
                      value={manager.membershipType.dues}
                      onChange={(e) => manager.updateValue('dues', parseFloat(e.target.value))}
                      placeholder="0.00"
                    />
                    {manager.fieldErrors.includes('overview:dues') && (
                      <div className="invalid-feedback">Annual Dues must be positive.</div>
                    )}
                  </div>
                </div>
                <div className="mb-2">
                  <label htmlFor="description" className={styles.label}>
                    Description
                  </label>
                  <textarea
                    id="description"
                    className={`form-control ${manager.fieldErrors.includes('overview:description') ? 'is-invalid' : ''}`}
                    value={manager.membershipType.description}
                    onChange={(e) => manager.updateValue('description', e.target.value)}
                    rows={3}
                    placeholder="Describe the membership benefits..."
                  />
                </div>
                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="familyMembers"
                        checked={manager.membershipType.familyMembers}
                        onChange={(e) => manager.updateValue('familyMembers', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="familyMembers">
                        Allows Family Members
                      </label>
                      <small className="form-text text-muted d-block">
                        Check if this membership type supports family members
                      </small>
                    </div>
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-6">
                    <label htmlFor="sortOrder" className={styles.label}>
                      Sort Order
                    </label>
                    <input
                      type="number"
                      id="sortOrder"
                      min="0"
                      className={`form-control ${manager.fieldErrors.includes('overview:sortOrder') ? 'is-invalid' : ''}`}
                      value={manager.membershipType.sortOrder}
                      onChange={(e) =>
                        manager.updateValue('sortOrder', parseInt(e.target.value, 10))
                      }
                      placeholder="e.g., 1"
                    />
                  </div>
                </div>
              </div>
              {/* Actions Card */}
              <div className={styles.actionCard}>
                <div className={styles.actionButtons}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={manager.saveChanges}
                    disabled={manager.isSaving}>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save
                    {manager.isSaving && <Spinner size="sm" animation="border" className="ms-2" />}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={manager.cancelChanges}
                    disabled={manager.isSaving}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      manager.back();
                    }}
                    disabled={manager.isSaving}>
                    Back
                  </button>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={manager.saveAndBack}
                    disabled={manager.isSaving}>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save & Back
                    {manager.isSaving && <Spinner size="sm" animation="border" className="ms-2" />}
                  </button>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={manager.saveAndNew}
                    disabled={manager.isSaving}>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save & Add New
                    {manager.isSaving && <Spinner size="sm" animation="border" className="ms-2" />}
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </LoadingContainer>
    </>
  );
};
